import './ChangeCountsRow.scss';
import { ChangeCount } from './ChangeCount';
import classNames from 'classnames';
import { getSonarObjectTypeLabelPlural } from '@sonar-software/sonar-ts-lib';
import { getTimelineSummarySelector } from '@timeline/@arch/selectors';
import { ObjectTypeChangeCounts } from './ChangeRecordsFilter';
import { SonarObjectType } from '@sonar-software/sonar-ts-types';
import { SonarText } from '@ui/text';
import { TimelineChangeType } from '@sonar-software/sonar-core';
import { Col, Row } from 'antd';
import React, { useCallback } from 'react';
import { TIMELINE_CHANGE_TYPE_ORDER, TimelineObjectFilter } from '@timeline/@arch/lib';
import { useBoolean, useSonarSelector } from '@hooks';

export interface ChangeCountsRowProps {
	counts: ObjectTypeChangeCounts;
	highlightedChangeType?: TimelineChangeType;
	onClick: (changeType: TimelineChangeType, sonarObjectType: SonarObjectType) => void;
	onClickObjectType: (sonarObjectType: SonarObjectType) => void;
	params: { objectFilter?: TimelineObjectFilter };
}

export const ChangeCountsRow: React.FC<ChangeCountsRowProps> = ({
	counts,
	highlightedChangeType,
	onClick,
	onClickObjectType,
	params
}) => {
	const { sonarObjectType, ...changes } = counts;
	const summary = useSonarSelector(getTimelineSummarySelector.data);
	const title = getSonarObjectTypeLabelPlural(sonarObjectType) ?? '';
	const [ highlight, setHighlight ] = useBoolean();

	const { discovered, modified, vanished } = changes;
	const rowCounts = [ discovered, modified, vanished ];
	const filtered = Boolean(Object.keys(params.objectFilter ?? {}).length);

	const handleOnClickType = useCallback(() => {
		onClickObjectType(sonarObjectType);
	}, [ onClickObjectType, sonarObjectType ]);

	return (
		<div className='change-counts-row'>
			<Row className='object-type-label'>
				<SonarText
					className='h3 clickable'
					onClick={ handleOnClickType }
					onMouseEnter={ setHighlight.on }
					onMouseLeave={ setHighlight.off }>
					{ title }
				</SonarText>
			</Row>
			<Row
				className={ classNames('change-counts', { highlight }) }
				gutter={ 8 }
				justify='space-evenly'
				wrap={ false }>
				{
					rowCounts.map((changeCount, index) => {
						const changeType = TIMELINE_CHANGE_TYPE_ORDER[ index ] || TimelineChangeType.Discovered;
						const key = `${sonarObjectType}-${changeType}`;
						const selected = params.objectFilter?.[ sonarObjectType ]?.[ changeType ];
						const highlighted = highlightedChangeType === changeType;

						return (
							<Col
								key={ key }
								span={ 8 }>
								<ChangeCount
									changeType={ changeType }
									count={ changeCount }
									filtered={ filtered }
									highlighted={ highlighted }
									loading={ !summary }
									objectType={ sonarObjectType }
									onClick={ onClick }
									selected={ selected } />
							</Col>
						);
					})
				}
			</Row>
		</div>
	);
};
